import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { PartialComment } from '../types';

import { deleteComment } from './deleteComment';

type Result = ModelHookResult<
	{
		error: boolean;
	},
	{
		deleteComment: () => Promise<boolean>;
	}
>;

export function useDeleteComment(comment: PartialComment): Result {
	const utilityContext = useUtilityContext();

	const { error, mutateAsync, isPending } = useMutation({
		mutationFn: async () => deleteComment(utilityContext, comment.id),
	});

	return {
		data: {
			error: !!error,
		},
		actions: {
			deleteComment: useCallback(async () => {
				if (isPending) return false;

				try {
					await mutateAsync();
					return true;
				} catch (err) {
					const deleteError = err as Error;
					deleteError.message = `Failed to delete comment: ${deleteError.message}`;
					void utilityContext.errorReporter.report(deleteError);
					return false;
				}
			}, [mutateAsync, isPending, utilityContext]),
		},
	};
}
